import * as React from "react";
import { Users, UserPlus, Mail, MoreHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

/**
 * TeamPage - Team management
 *
 * Features:
 * - Team member cards
 * - Role badges
 */
export function TeamPage() {
  const { t } = useTranslation("dashboard");

  const placeholderMembers = [
    {
      name: "Alex Johnson",
      email: "alex@example.com",
      role: "Admin",
      avatar: null,
    },
    {
      name: "Sarah Chen",
      email: "sarah@example.com",
      role: "Developer",
      avatar: null,
    },
    {
      name: "Mike Wilson",
      email: "mike@example.com",
      role: "Designer",
      avatar: null,
    },
    {
      name: "Emily Davis",
      email: "emily@example.com",
      role: "Developer",
      avatar: null,
    },
  ];

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case "Admin":
        return "bg-primary/10 text-primary border-primary/20";
      case "Developer":
        return "bg-emerald-500/10 text-emerald-600 border-emerald-500/20";
      case "Designer":
        return "bg-purple-500/10 text-purple-600 border-purple-500/20";
      default:
        return "bg-muted text-muted-foreground border-border";
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            {t("pages.team.title")}
          </h1>
          <p className="text-base text-muted-foreground md:text-lg">
            {t("pages.team.description")}
          </p>
        </div>
        <Button>
          <UserPlus className="mr-2 h-4 w-4" />
          Invite Member
        </Button>
      </div>

      <Separator />

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-primary/20 bg-primary/10">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-foreground">
                  {placeholderMembers.length}
                </p>
                <p className="text-sm text-muted-foreground">Team Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Team members */}
      <section className="space-y-4">
        <h2 className="text-xs font-medium tracking-widest text-muted-foreground uppercase">
          Team Members
        </h2>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {placeholderMembers.map((member) => (
            <Card key={member.email}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <Avatar className="h-12 w-12 border border-border">
                    <AvatarImage src={member.avatar || undefined} />
                    <AvatarFallback className="bg-primary/10 text-sm font-medium text-primary">
                      {getInitials(member.name)}
                    </AvatarFallback>
                  </Avatar>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </div>
                <div className="mt-4">
                  <h3 className="font-medium text-foreground">{member.name}</h3>
                  <div className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Mail className="h-3.5 w-3.5" />
                    <span className="truncate">{member.email}</span>
                  </div>
                </div>
                <div className="mt-3">
                  <span
                    className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${getRoleColor(member.role)}`}
                  >
                    {member.role}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}

export default TeamPage;
